/**
 * Geofences Download Section Component
 * 
 * Section for downloading and importing geofences from UAT/Production environments.
 * Follows the same pattern as StopsDownloadSection but adapted for geofences.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import { fetchGeofences, fetchNetworkSettings, type UATNetwork, type UATNetworkSettingsResponse } from '../../../services/fixedRouteUatClient';
import {
  importUATGeofences,
  type GeofenceImportResult,
} from '../../../services/fixedRouteUatImporter';
import { AppContext } from '../../../context/context';
import { type Environment, getEnvironmentDisplayName } from './EnvironmentSelector';
import type { UATGeofence } from '../../../services/fixedRouteUatClient';
import { getCachedGeofencesList, setCachedGeofencesList } from './geofencesCache';

import { Icon } from '@liftango/ui-icons';
interface GeofencesDownloadSectionProps {
  jwtToken: string;
  environment: Environment;
  networks: UATNetwork[];
  isLoadingNetworks: boolean;
  networkError: string | null;
  selectedProductId: string;
  selectedNetworkId: number | null;
  onProductChange: (productId: string) => void;
  onLoadNetworks: () => void;
  serviceDate: string;
  onServiceDateChange: (date: string) => void;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  downloadAcknowledged: boolean;
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

/**
 * Helper function to get a stable ID from a geofence object
 * Uses id or geofenceId from the API response, or generates a stable fallback
 */
function getGeofenceId(geofence: UATGeofence): string {
  // Use id or geofenceId from API response (stable identifiers)
  if (geofence.id) return geofence.id;
  const geofenceAny = geofence as any;
  if (geofenceAny.geofenceId) return geofenceAny.geofenceId;
  // Fallback: use name if available, otherwise generate a stable ID based on name
  if (geofence.name) return `geofence-${geofence.name}`;
  // Last resort: generate based on first point coordinates (stable)
  const geofenceAny2 = geofence as any;
  if (geofenceAny2.points && geofenceAny2.points.length > 0) {
    const firstPoint = geofenceAny2.points[0];
    if (firstPoint.latitude && firstPoint.longitude) {
      return `geofence-${firstPoint.latitude}-${firstPoint.longitude}`;
    }
  }
  // Final fallback (should rarely happen)
  return `geofence-unknown-${JSON.stringify(geofence).substring(0, 50)}`;
}

export const GeofencesDownloadSection: React.FC<GeofencesDownloadSectionProps> = ({
  jwtToken,
  environment,
  networks,
  isLoadingNetworks,
  networkError,
  selectedProductId,
  selectedNetworkId,
  onProductChange,
  onLoadNetworks,
  serviceDate,
  onServiceDateChange,
  timezone,
  onTimezoneChange,
  downloadAcknowledged,
  onDownloadAcknowledgedChange,
}) => {
  const { t } = useTranslation('mapDownload');
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('GeofencesDownloadSection must be used within AppContext');
  }

  // Date range state (required for geofences API)
  // Initialize dates first so we can use them in state initializers below
  const initialStartDate = serviceDate || '';
  const initialEndDate = (() => {
    if (serviceDate) {
      const date = new Date(serviceDate);
      date.setDate(date.getDate() + 30);
      return date.toISOString().split('T')[0];
    }
    return '';
  })();
  
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);

  // Network settings for timezone-agnostic pattern
  const [networkSettings, setNetworkSettings] = useState<UATNetworkSettingsResponse | null>(null);
  const [isLoadingNetworkSettings, setIsLoadingNetworkSettings] = useState<boolean>(false);

  // Calculate effective timezone from network settings
  const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;

  const [geofenceSearchQuery, setGeofenceSearchQuery] = useState<string>('');

  // Download state - Initialize from cache immediately to prevent flash
  const [geofencesList, setGeofencesList] = useState<UATGeofence[]>(() => {
    // Check cache synchronously during initialization
    if (jwtToken.trim() && selectedProductId && initialStartDate && initialEndDate) {
      const cached = getCachedGeofencesList(environment, selectedProductId, initialStartDate, initialEndDate);
      return cached || [];
    }
    return [];
  });
  const [selectedGeofenceIds, setSelectedGeofenceIds] = useState<Set<string>>(() => {
    // Initialize selected IDs from cached data
    if (jwtToken.trim() && selectedProductId && initialStartDate && initialEndDate) {
      const cached = getCachedGeofencesList(environment, selectedProductId, initialStartDate, initialEndDate);
      if (cached && cached.length > 0) {
        return new Set(cached.map((gf: UATGeofence) => getGeofenceId(gf)));
      }
    }
    return new Set();
  });
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [expandedGeofenceIds, setExpandedGeofenceIds] = useState<Set<string>>(new Set());

  // Import state
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [importStep, setImportStep] = useState<string>('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<GeofenceImportResult[] | null>(null);

  // Load network settings when product is selected (timezone-agnostic pattern)
  useEffect(() => {
    if (selectedProductId && jwtToken.trim() && !isLoadingNetworkSettings) {
      handleLoadNetworkSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, jwtToken]);

  // Update parent timezone when network settings load
  useEffect(() => {
    if (networkSettings?.data?.networks?.[0]?.networkSettings?.timezone) {
      const networkTimezone = networkSettings.data.networks[0].networkSettings.timezone;
      if (networkTimezone !== timezone) {
        onTimezoneChange(networkTimezone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkSettings]);

  // Update startDate when serviceDate changes
  useEffect(() => {
    if (serviceDate && serviceDate !== startDate) {
      setStartDate(serviceDate);
      // Update end date to 30 days after start date
      const date = new Date(serviceDate);
      date.setDate(date.getDate() + 30);
      setEndDate(date.toISOString().split('T')[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDate]);

  // Track previous jwtToken and environment to only reset when they actually change
  const prevJwtTokenRef = useRef<string>(jwtToken);
  const prevEnvironmentRef = useRef<Environment>(environment);

  // Reset state when environment or JWT token changes (but not on initial mount)
  useEffect(() => {
    const jwtTokenChanged = prevJwtTokenRef.current !== jwtToken;
    const environmentChanged = prevEnvironmentRef.current !== environment;

    if (jwtTokenChanged || environmentChanged) {
      // Only reset if values actually changed (not on initial mount)
      setGeofencesList([]);
      setSelectedGeofenceIds(new Set());
      setDownloadError(null);
      setImportResults(null);
      setImportError(null);
      
      // Update refs
      prevJwtTokenRef.current = jwtToken;
      prevEnvironmentRef.current = environment;
    } else {
      // On initial mount, just update refs without resetting
      prevJwtTokenRef.current = jwtToken;
      prevEnvironmentRef.current = environment;
    }
  }, [jwtToken, environment]);

  // Auto-download when component mounts or key parameters change (only if no data loaded)
  useEffect(() => {
    if (
      jwtToken.trim() &&
      selectedProductId &&
      startDate &&
      endDate &&
      !isDownloading &&
      geofencesList.length === 0
    ) {
      // Only trigger download if we don't have data (cache was already checked in state initialization)
      const timer = setTimeout(() => {
        handleDownload();
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, startDate, endDate, jwtToken, environment]);

  const handleLoadNetworkSettings = async () => {
    if (!jwtToken.trim() || !selectedProductId) return;

    setIsLoadingNetworkSettings(true);
    try {
      const settings = await fetchNetworkSettings(jwtToken, selectedProductId, environment);
      setNetworkSettings(settings);
    } catch (error) {
      console.warn('[GeofencesDownloadSection] Failed to load network settings:', error);
      // Don't set error - this is optional for timezone
    } finally {
      setIsLoadingNetworkSettings(false);
    }
  };

  const handleDownload = async () => {
    if (!jwtToken.trim()) { setDownloadError(t('common.errors.jwtRequired')); return; }
    if (!selectedProductId) { setDownloadError(t('common.errors.productRequired')); return; }
    if (!startDate) { setDownloadError(t('common.errors.startDateRequired')); return; }
    if (!endDate) { setDownloadError(t('common.errors.endDateRequired')); return; }
    if (new Date(endDate) < new Date(startDate)) {
      setDownloadError(t('common.errors.endBeforeStart')); return;
    }

    // Check cache first before clearing state
    const cachedGeofences = getCachedGeofencesList(environment, selectedProductId, startDate, endDate);
    if (cachedGeofences && cachedGeofences.length > 0) {
      console.log('[GeofencesDownloadSection] Using cached geofences:', cachedGeofences.length);
      setGeofencesList(cachedGeofences);
      // Select all geofences by default
      const geofenceIds = cachedGeofences.map((gf: UATGeofence) => getGeofenceId(gf));
      setSelectedGeofenceIds(new Set(geofenceIds));
      setDownloadError(null);
      return;
    }

    // No cache - proceed with API call
    setIsDownloading(true);
    setDownloadError(null);
    setGeofencesList([]);
    setSelectedGeofenceIds(new Set());

    try {

      console.log('[GeofencesDownloadSection] Fetching geofences...', {
        productId: selectedProductId,
        startDate,
        endDate,
      });
      
      const geofences = await fetchGeofences(
        jwtToken,
        selectedProductId,
        startDate,
        endDate,
        environment
      );

      console.log('[GeofencesDownloadSection] Fetched geofences:', {
        count: geofences.length,
        geofences: geofences,
      });

      // Cache the response
      setCachedGeofencesList(environment, selectedProductId, startDate, endDate, geofences);

      setGeofencesList(geofences);

      // Select all geofences by default
      const geofenceIds = geofences.map((gf: UATGeofence) => getGeofenceId(gf));
      console.log('[GeofencesDownloadSection] Generated geofence IDs:', geofenceIds);
      setSelectedGeofenceIds(new Set(geofenceIds));

      // Clear any previous errors
      setDownloadError(null);

    } catch (error) {
      let errorMessage = t('geofences.errors.downloadFailedBase', { environment: getEnvironmentDisplayName(environment) });
      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.message.includes('401') || error.message.includes('Authentication')) {
          errorMessage = t('common.errors.authFailed');
        } else if (error.message.includes('404')) {
          errorMessage = t('geofences.errors.notFound');
        } else if (error.message.includes('429')) {
          errorMessage = t('common.errors.tooManyRequests');
        } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
          errorMessage = t('common.errors.serverError', { environment: getEnvironmentDisplayName(environment) });
        }
      }
      setDownloadError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImport = async () => {
    // Auto-select all if nothing is selected
    let geofencesToImport = Array.from(selectedGeofenceIds).map(id => geofencesList.find(gf => getGeofenceId(gf) === id)).filter(Boolean) as typeof geofencesList;
    if (geofencesToImport.length === 0) {
      geofencesToImport = geofencesList; // If nothing selected, import all
      setSelectedGeofenceIds(new Set(geofencesList.map(getGeofenceId))); // Update UI selection
    }

    if (!context) {
      setImportError(t('common.errors.contextNotAvailable'));
      return;
    }

    setIsImporting(true);
    setImportError(null);
    setImportProgress(0);
    setImportStep(t('geofences.stepInitializing'));

    try {
      // Use geofencesToImport (either selected or all)
      setImportStep(t('geofences.stepImporting', { count: geofencesToImport.length }));
      setImportProgress(50);

      // Import geofences
      const results = importUATGeofences(geofencesToImport, context);

      setImportProgress(100);
      setImportStep(t('geofences.stepComplete'));

      setImportResults(results);

      // Clear selection after successful import
      setSelectedGeofenceIds(new Set());

    } catch (error) {
      console.error('[GeofencesDownloadSection] Import error:', error);
      setImportError(error instanceof Error ? error.message : t('geofences.errors.importGeneric'));
    } finally {
      setIsImporting(false);
    }
  };

  const selectedProduct = networks.find(p => p.productId === selectedProductId);
  const totalSelectedGeofences = selectedGeofenceIds.size;
  // Import button is always available if geofences are present (will auto-select all if nothing selected)
  const canImport = geofencesList.length > 0 && !isImporting;
  const canDownload = jwtToken.trim() && selectedProductId && startDate && endDate && !isDownloading;

  // Filter geofences based on search query
  const filteredGeofences = geofencesList.filter((geofence) => {
    if (!geofenceSearchQuery) return true;
    const searchLower = geofenceSearchQuery.toLowerCase();
    const geofenceId = geofence.id || '';
    const geofenceName = geofence.name || '';
    const geofenceType = geofence.type || '';
    return (
      geofenceName.toLowerCase().includes(searchLower) ||
      geofenceId.toLowerCase().includes(searchLower) ||
      geofenceType.toLowerCase().includes(searchLower)
    );
  });

  // Helper function to get geometry summary
  const getGeometrySummary = (geofence: UATGeofence): string => {
    if (geofence.geometry) {
      const geom = geofence.geometry;
      if (geom.type === 'Circle' || geom.type === 'circle') {
        return t('geofences.geometry.circle', { radius: geom.radius ?? 'unknown' });
      } else if (geom.type === 'Polygon' || geom.type === 'polygon') {
        const coords = geom.coordinates;
        if (Array.isArray(coords)) {
          return t('geofences.geometry.polygon', { count: coords.length });
        }
        return t('geofences.geometry.polygonNoCount');
      }
      return geom.type || t('geofences.geometry.unknown');
    }
    return t('geofences.geometry.none');
  };

  // Show placeholder if consent not acknowledged
  if (!downloadAcknowledged) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: '100%',
        flex: 1,
        minHeight: 0,
        p: 2,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <img
          src="/icons/consent-img.png"
          alt={t('common.consentImageAlt')}
          style={{ 
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            objectPosition: 'center center'
          }} 
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Left Column - Summary Section (when geofences are present) */}
        {geofencesList.length > 0 && (
          <Box
            sx={{
              width: '45%',
              minWidth: 280,
              display: 'flex',
              flexDirection: 'column',
              position: 'sticky',
              top: 0,
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              maxHeight: 'calc(100vh - 200px)',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('geofences.summaryAboutToImport', { count: totalSelectedGeofences > 0 ? totalSelectedGeofences : geofencesList.length })}
            </Typography>
            {selectedProduct && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('geofences.network', { name: selectedProduct.productName })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('geofences.dateRange', { start: startDate, end: endDate })}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Import Button - Always visible above the scrollable list */}
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleImport}
              disabled={!canImport}
              fullWidth
              startIcon={isImporting ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
              sx={{ mb: 2 }}
            >
              {isImporting
                ? t('common.importing')
                : totalSelectedGeofences > 0
                  ? t('geofences.importButton', { count: totalSelectedGeofences })
                  : t('geofences.importAllButton', { count: geofencesList.length })
              }
            </Button>

            {isImporting && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={importProgress} />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {importStep || t('geofences.importingGeofences')} ({Math.round(importProgress)}%)
                </Typography>
              </Box>
            )}

            {importError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>{t('common.importFailed')}</AlertTitle>
                {importError}
              </Alert>
            )}

            {importResults && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>{t('common.importComplete')}</AlertTitle>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  <strong>{t('geofences.results.label')}</strong>{' '}
                  {t('geofences.results.summary', {
                    created: importResults.filter(r => r.action === 'created').length,
                    skipped: importResults.filter(r => r.action === 'skipped').length,
                  })}
                </Typography>
                {importResults.filter(r => r.action === 'skipped').length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                      {t('geofences.results.skippedTitle', { count: importResults.filter(r => r.action === 'skipped').length })}
                    </Typography>
                    {importResults
                      .filter(r => r.action === 'skipped')
                      .slice(0, 5)
                      .map((result) => (
                        <Typography key={result.geofenceId} variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                          {t('geofences.results.skippedItem', { id: result.geofenceId, reason: result.reason || t('common.unknownReason') })}
                        </Typography>
                      ))}
                    {importResults.filter(r => r.action === 'skipped').length > 5 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                        {t('common.andMoreCount', { max: importResults.filter(r => r.action === 'skipped').length - 5 })}
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
            )}
            
            <List 
              dense 
              disablePadding
              sx={{
                flex: 1,
                overflowY: 'auto',
              }}
            >
              {geofencesList
                .filter(gf => {
                  const gfId = getGeofenceId(gf);
                  return selectedGeofenceIds.has(gfId);
                })
                .map((geofence) => {
                  const geofenceId = getGeofenceId(geofence);
                  const isExpanded = expandedGeofenceIds.has(geofenceId);
                  return (
                    <Accordion
                      key={geofenceId}
                      expanded={isExpanded}
                      onChange={() => {
                        const newExpanded = new Set(expandedGeofenceIds);
                        if (isExpanded) {
                          newExpanded.delete(geofenceId);
                        } else {
                          newExpanded.add(geofenceId);
                        }
                        setExpandedGeofenceIds(newExpanded);
                      }}
                      sx={{
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        border: 'none',
                        '&.Mui-expanded': { margin: 0 },
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<Icon size={20} name="chevron-down" />}
                        sx={{
                          px: 0,
                          py: 1,
                          minHeight: 'auto',
                          '&.Mui-expanded': { minHeight: 'auto' },
                          '& .MuiAccordionSummary-content': {
                            my: 0,
                            '&.Mui-expanded': { my: 0 },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: '#ff6b6b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography sx={{ color: 'white', fontSize: 20 }}>G</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {geofence.name || geofenceId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getGeometrySummary(geofence)}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pb: 1.5, pt: 0, pl: 5.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('geofences.detail.id', { id: geofenceId })}
                          </Typography>
                          {geofence.type && (
                            <Typography variant="caption" color="text.secondary">
                              {t('geofences.detail.type', { type: geofence.type })}
                            </Typography>
                          )}
                          {geofence.geometry?.center && (
                            <Typography variant="caption" color="text.secondary">
                              {t('geofences.detail.center', { lat: geofence.geometry.center.latitude.toFixed(6), lng: geofence.geometry.center.longitude.toFixed(6) })}
                            </Typography>
                          )}
                          {geofence.geometry?.radius && (
                            <Typography variant="caption" color="text.secondary">
                              {t('geofences.detail.radius', { radius: geofence.geometry.radius })}
                            </Typography>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
            </List>
          </Box>
        )}

        {/* Right Column - Controls */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            {networkError && (
              <Alert severity="error">
                {networkError}
              </Alert>
            )}

            {/* Show loading state during initial auto-download */}
            {isDownloading && geofencesList.length === 0 && !downloadError && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {t('geofences.loadingGeofences')}
                </Typography>
              </Box>
            )}

            {/* Date Range Selection - Hide during initial auto-download */}
            {!(isDownloading && geofencesList.length === 0) && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('geofences.startDateLabel')}
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (e.target.value) {
                      onServiceDateChange(e.target.value);
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  disabled={isDownloading || isImporting}
                />
                <TextField
                  label={t('geofences.endDateLabel')}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  disabled={isDownloading || isImporting}
                  error={!!(endDate && startDate && new Date(endDate) < new Date(startDate))}
                  helperText={endDate && startDate && new Date(endDate) < new Date(startDate) ? t('common.errors.endBeforeStart') : ''}
                />
              </Box>
            )}

            {/* Download Button - Hide during initial auto-download */}
            {!(isDownloading && geofencesList.length === 0) && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                disabled={!canDownload}
                fullWidth
                startIcon={isDownloading ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
              >
                {isDownloading ? t('common.downloading') : t('geofences.downloadButton')}
              </Button>
            )}

            {/* Show loading indicator only when downloading with existing data (manual download) */}
            {isDownloading && geofencesList.length > 0 && (
              <Box>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {t('common.loadingPreview')}
                </Typography>
              </Box>
            )}

            {downloadError && (
              <Alert severity="error">
                <AlertTitle>{t('common.downloadFailed')}</AlertTitle>
                {downloadError}
              </Alert>
            )}

            {/* Preview Section */}
            {geofencesList.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        if (selectedGeofenceIds.size === geofencesList.length) {
                          setSelectedGeofenceIds(new Set());
                        } else {
                          setSelectedGeofenceIds(new Set(geofencesList.map((gf: UATGeofence) => getGeofenceId(gf))));
                        }
                      }}
                      disabled={isImporting}
                      sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                    >
                      {selectedGeofenceIds.size === geofencesList.length ? t('common.deselectAll') : t('common.selectAll')}
                    </Button>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('geofences.previewLabel', { count: geofencesList.length })}
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('geofences.searchPlaceholder')}
                    value={geofenceSearchQuery}
                    onChange={(e) => setGeofenceSearchQuery(e.target.value)}
                    disabled={isImporting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon name="magnifying-glass" size={20} />
                        </InputAdornment>
                      ),
                      endAdornment: geofenceSearchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setGeofenceSearchQuery('')}
                            edge="end"
                          >
                            <Icon name="xmark" size={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />

                  <List 
                    dense 
                    sx={{ 
                      maxHeight: '400px',
                      overflow: 'auto',
                      mb: 2,
                    }}
                  >
                    {filteredGeofences.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary={t('geofences.noGeofencesFound')}
                          secondary={geofenceSearchQuery ? t('common.tryAdjustingSearch') : t('geofences.noGeofencesAvailable')}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ) : (
                      filteredGeofences.map((geofence) => {
                        const geofenceId = getGeofenceId(geofence);
                        const isSelected = selectedGeofenceIds.has(geofenceId);
                        return (
                          <ListItem key={geofenceId} sx={{ py: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedGeofenceIds);
                                    if (e.target.checked) {
                                      newSet.add(geofenceId);
                                    } else {
                                      newSet.delete(geofenceId);
                                    }
                                    setSelectedGeofenceIds(newSet);
                                  }}
                                  disabled={isImporting}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      bgcolor: '#ff6b6b',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography sx={{ color: 'white', fontSize: 20 }}>G</Typography>
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {geofence.name || geofenceId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {getGeometrySummary(geofence)}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </Box>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default GeofencesDownloadSection;

